import { GridSortDirection } from "@mui/x-data-grid";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { AccountSelf } from "../types/account";
import { SecLevel } from "../types/seclevel";
const API_URL = import.meta.env.VITE_PORTAL_BACKEND_URL; //process.env.REACT_APP_PORTAL_BACKEND_URL;
const API_VERSION = import.meta.env.VITE_PORTAL_BACKEND_VERSION;

export const HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

const axiosInstance = axios.create({
  baseURL: API_URL + `/api/${API_VERSION}`,
  headers: HEADERS,
  withCredentials: true, // send cookies
});

export type LoginArgs = {
  username: string;
  password: string;
};

export type LoginResponse = {
  userId: number;
};

export type LogoutResponse = {
  success: true;
};

export type ErrorResponse = {
  detail: string;
};

/**
 * Makes a POST request to the /login endpoint. If successful, the server returns a cookie
 * containing the JWT access token used to access protected endpoints.
 *
 * @returns Promise
 */
export const login = ({
  username,
  password,
}: LoginArgs): Promise<LoginResponse> => {
  return axiosInstance
    .post("/login/", { username, passwort: password })
    .then((res) => res.data);
};

/**
 * Makes a GET request to the /logout endpoint. If successful, the server deletes the JWT Cookie.
 */
export const logout = (): Promise<LogoutResponse> => {
  return axiosInstance.get("/logout/").then((res) => res.data);
};

/**
 * Generic Endpoint class, for account and webmap endpoints.
 * First generic contains attributes visible for admins, the second one
 * only those accessible to regular users (are equal by default).
 */
class Endpoint<T extends { id: number }, U = T> {
  constructor(protected endpointPath: string) {}
  get(id: number, config?: AxiosRequestConfig): Promise<T> {
    return axiosInstance
      .get(this.endpointPath + "/" + id, config)
      .then((res) => res.data);
  }

  getMe(config?: AxiosRequestConfig): Promise<U> {
    return axiosInstance
      .get(this.endpointPath + "/me", config)
      .then((res) => res.data);
  }

  getAll(
    orderBy?: string,
    orderType?: GridSortDirection,
    limit?: number,
    offset?: number,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T[]>> {
    if (orderType === undefined) {
      orderType = "asc";
    }
    return axiosInstance.get(this.endpointPath + "/", {
      params: { orderBy, orderType, limit, offset },
      ...config,
    });
  }
  update(
    id: number,
    changes: Partial<T>,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return axiosInstance
      .patch(`${this.endpointPath}/${id}`, changes, config)
      .then((res) => res.data);
  }
  createNew(object: Partial<T>, config?: AxiosRequestConfig): Promise<T> {
    return axiosInstance
      .post(this.endpointPath, object, config)
      .then((res) => res.data);
  }
}

export class SecLevelEndpoint {
  constructor(
    protected endpointPath: string,
    protected axiosInstance: AxiosInstance
  ) {}

  getAll(
    orderBy?: string,
    orderType?: GridSortDirection,
    limit?: number,
    offset?: number,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<SecLevel[]>> {
    if (orderType === undefined) {
      orderType = "asc";
    }
    return this.axiosInstance.get(this.endpointPath + "/", {
      params: { orderBy, orderType, limit, offset },
      ...config,
    });
  }
  update(
    id: number,
    changes: Partial<SecLevel>,
    config?: AxiosRequestConfig
  ): Promise<SecLevel> {
    return this.axiosInstance
      .patch(`${this.endpointPath}/${id}`, changes, config)
      .then((res) => res.data);
  }
}

export interface Webmap {
  id: number;
  ars_lnd: string;
  bezeichner: string;
  kunden_id: string;
  projekt_id: string;
  intern: boolean;
  titel: string;
  map_setup: string;
  kbs: string;
  is_3d: boolean;
  aktiv: boolean;
  factura: boolean;
  dauer: number;
  fs_start: string;
  fs_ende: string;
  changed_by: string;
  is_tagged: boolean;
  is_revised: boolean;
  is_locked: boolean;
  is_checked: boolean;
  is_removed: boolean;
}

interface WebmapWithAccounts extends Webmap {
  accounts: Account[];
}

export interface Account {
  id: number;
  admin: boolean;
  kontakt_id: string;
  kunden_id: string;
  intern: boolean;
  cookies_1: boolean;
  cookies_2: boolean;
  dse: boolean;
  vnb: boolean;
  faktura: boolean;
  vorname: string;
  nachname: string;
  username: string;
  aktiv: boolean;
  is_removed: boolean;
  position: string;
  institut: string;
  stn: string;
  hnr: number;
  hnrz: string;
  plz: string;
  ort: string;
  ortz: string;
  email: string;
  mobil: string;
  telefon: string;
  changed_by: string;
  created_by: string;
  webmaps: {
    id: number;
  }[];
}

export type WebmapSelf = {
  id: number;
  ars_lnd: string;
  bezeichner: string;
  backend_url_prod: string;
  backend_port_dev: string;
  frontend_url_prod: string;
  frontend_port_dev: string;
};

interface WebmapWithAccounts extends Webmap {
  accounts: Account[];
}

interface AccountWithWebmaps extends Webmap {
  webmaps: Webmap[];
}

/**
 * The Accounts endpoint. Extends generic Endpoint class by adding the webmaps
 * endpoints as well as a patch method for changing information about onself.
 */
class AccountsEndpoint extends Endpoint<Account, AccountSelf> {
  patchMe(
    changes: Partial<AccountSelf>,
    config?: AxiosRequestConfig
  ): Promise<AccountSelf> {
    return axiosInstance
      .patch(this.endpointPath + "/me", changes, config)
      .then((res) => res.data);
  }
  getWebmaps(
    accountId: number,
    config?: AxiosRequestConfig
  ): Promise<Webmap[]> {
    return axiosInstance
      .get(`${this.endpointPath}/${accountId}/webmaps`, config)
      .then((res) => res.data);
  }
  addWebmap(
    accountId: number,
    webMapId: number,
    config?: AxiosRequestConfig
  ): Promise<AccountWithWebmaps> {
    return axiosInstance
      .put(`${this.endpointPath}/${accountId}/webmaps/${webMapId}`, config)
      .then((res) => res.data);
  }
  putWebmaps(
    accountId: number,
    webMapIds: number[],
    config?: AxiosRequestConfig
  ): Promise<AccountWithWebmaps> {
    return axiosInstance
      .put(`${this.endpointPath}/${accountId}/webmaps`, webMapIds, config)
      .then((res) => res.data);
  }
  removeWebmap(
    accountId: number,
    webMapId: number,
    config?: AxiosRequestConfig
  ): Promise<AccountWithWebmaps> {
    return axiosInstance
      .put(`${this.endpointPath}/${accountId}/webmaps/${webMapId}`, config)
      .then((res) => res.data);
  }
}

/**
 * The Webmaps endpoint. Extends generic Endpoint class by adding the accounts
 * endpoints.
 */
class WebmapsEndpoint extends Endpoint<Webmap, WebmapSelf[]> {
  getAccounts(
    webmapId: number,
    config?: AxiosRequestConfig
  ): Promise<Account[]> {
    return axiosInstance
      .get(`${this.endpointPath}/${webmapId}/accounts`, config)
      .then((res) => res.data);
  }
  addAccount(
    webmapId: number,
    accountId: number,
    config?: AxiosRequestConfig
  ): Promise<WebmapWithAccounts> {
    return axiosInstance
      .put(`${this.endpointPath}/${webmapId}/accounts/${accountId}`, config)
      .then((res) => res.data);
  }
  removeAccount(
    webMapId: number,
    accountId: number,
    config?: AxiosRequestConfig
  ): Promise<WebmapWithAccounts> {
    return axiosInstance
      .put(`${this.endpointPath}/${webMapId}/accounts/${accountId}`, config)
      .then((res) => res.data);
  }
}

export const Accounts = new AccountsEndpoint("/accounts");
export const Webmaps = new WebmapsEndpoint("/webmaps");
export const SecLevelsPortal = new SecLevelEndpoint(
  "/seclevels",
  axiosInstance
);
